import { useMemo } from 'react';
import { normalizeToMonthly } from '../utils/finance';

// --- HELPERS ---
const toLocalDateStr = (dateObj) => {
    if (!dateObj) return "";
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const calculateAdditionalIncomeNet = (stream) => {
    let gross = normalizeToMonthly(parseFloat(stream.amount)||0, stream.frequency || 'monthly');
    let deductionsTotal = 0;
    if (stream.inputType === 'gross') {
        (stream.deductions || []).forEach(d => {
            let amt = parseFloat(d.amount) || 0;
            if (d.type === 'percent') { amt = gross * (amt / 100); } 
            else { amt = normalizeToMonthly(amt, d.frequency || stream.frequency); }
            deductionsTotal += amt;
        });
    }
    return Math.max(0, gross - deductionsTotal);
};

export const useCalculationEngine = (data, trackerOffset, simulateVolatility) => {

    const calculatedPaycheck = useMemo(() => { 
        const gross = (data.salary || 0) / (data.payFrequency || 26); 
        let ded = 0; 
        data.deductions.forEach(d => { 
            ded += d.type === 'fixed' ? (d.category === 'Tax' ? (d.amount || 0) / data.payFrequency : (d.amount || 0)) : gross * ((d.amount || 0) / 100); 
        }); 
        return { grossPerPeriod: gross, netPerPeriod: gross - ded, monthlyNet: (gross - ded) * (data.payFrequency/12) }; 
    }, [data.salary, data.payFrequency, data.deductions]);

    const expenseAnalysis = useMemo(() => { 
        let burn = 0, trans = 0; 
        data.expenseItems.forEach(item => { 
            const val = normalizeToMonthly(item.amount || 0, item.frequency); 
            if (item.linkedAssetId || item.linkedDebtId) trans += val; else burn += val; 
        }); 
        return { burn, trans, total: burn + trans }; 
    }, [data.expenseItems]);
    
    const surplusCalculations = useMemo(() => { 
        const monthlyNet = calculatedPaycheck.monthlyNet || 0;
        const expenseTotal = expenseAnalysis.total || 0;
        let additionalBudgetIncome = 0;
        (data.additionalIncome || []).forEach(stream => {
            const net = calculateAdditionalIncomeNet(stream);
            if (!stream.flowTo || stream.flowTo === 'budget') { additionalBudgetIncome += net; }
        });
        const operationalSurplus = monthlyNet + additionalBudgetIncome - expenseTotal;
        let bonusMonthly = 0; 
        data.bonuses.forEach(b => { if(b.active) { 
            const raw = b.type === 'fixed' ? (b.amount || 0) : (data.salary || 0) * ((b.amount || 0)/100); 
            const net = raw * (1 - ((b.taxRate || 0)/100));
            bonusMonthly += net / 12; 
        } }); 
        return { operational: operationalSurplus, total: Math.max(0, operationalSurplus + bonusMonthly), additionalBudgetIncome }; 
    }, [calculatedPaycheck, expenseAnalysis, data.bonuses, data.salary, data.additionalIncome]);

    const waterfallVisuals = useMemo(() => { 
        let remaining = Math.max(0, surplusCalculations.operational || 0); 
        return data.waterfall.sort((a,b) => a.priority - b.priority).map(bucket => { 
            let filled = 0; 
            const asset = data.assets.find(a => a.id === bucket.linkedAssetId); 
            const currentVal = asset ? (asset.value || 0) : 0; 
            if (bucket.targetType === 'annual_limit') filled = Math.min(remaining, (bucket.targetAmount || 0) / 12); 
            else if (bucket.targetType === 'total_goal') filled = Math.min(remaining, Math.max(0, (bucket.targetAmount || 0) - currentVal)); 
            else filled = remaining; 
            remaining = Math.max(0, remaining - filled); 
            let checksNeeded = 0;
            const fixedBurnMonthly = data.expenseItems.filter(e => e.linkedAssetId === bucket.linkedAssetId).reduce((sum, e) => sum + normalizeToMonthly(e.amount, e.frequency), 0);
            const totalMonthlyInflow = filled + fixedBurnMonthly;
            const amountPerCheck = totalMonthlyInflow * (12 / (data.payFrequency || 26));
            if (bucket.targetType === 'total_goal' && amountPerCheck > 0) {
                const remainingGoal = Math.max(0, (bucket.targetAmount || 0) - currentVal);
                checksNeeded = Math.ceil(remainingGoal / amountPerCheck);
            }
            return { ...bucket, filled, remainingAfter: remaining, checksNeeded, currentVal }; 
        }); 
    }, [data.waterfall, surplusCalculations, data.assets, data.payFrequency, data.expenseItems]);

    const globalAllocation = useMemo(() => {
        const totalValue = data.assets.reduce((sum, a) => sum + (parseFloat(a.value) || 0), 0);
        if (totalValue === 0) return [];
        const keys = ['usStock', 'intlStock', 'bonds', 'cash', 'crypto', 'realEstate'];
        const sums = keys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
        const typeMap = { 'Real Estate': 'realEstate', 'Crypto': 'crypto', 'Checking': 'cash', 'Savings': 'cash', 'Cash': 'cash' };
        data.assets.forEach(asset => {
            const val = parseFloat(asset.value) || 0;
            let usedAlloc = { ...asset.allocation };
            const hasManual = Object.values(usedAlloc).some(v => parseFloat(v) > 0);
            if (!hasManual && typeMap[asset.subtype]) usedAlloc[typeMap[asset.subtype]] = 100;
            keys.forEach(key => { const allocPercent = parseFloat(usedAlloc?.[key] || 0); sums[key] += val * (allocPercent / 100); });
        });
        return keys.map(key => ({ name: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()), value: sums[key] })).filter(i => i.value > 0);
    }, [data.assets]);

    const trackerData = useMemo(() => { 
        const base = new Date(data.trackerPeriodStart);
        base.setHours(0,0,0,0);

        const start = new Date(base);
        if (data.trackerPayFreq === 'monthly') start.setMonth(start.getMonth() + trackerOffset);
        else start.setDate(base.getDate() + (trackerOffset * 14));
        start.setHours(0,0,0,0);

        const end = new Date(start);
        if (data.trackerPayFreq === 'monthly') { 
            end.setMonth(end.getMonth() + 1); 
            end.setDate(end.getDate() - 1); 
        } else { 
            end.setDate(start.getDate() + 13); 
        }
        end.setHours(23,59,59,999);
        
        const expected = data.expenseItems.filter(ex => { 
            // 1. Handle One-Time / Custom Date
            if (ex.frequency === 'one_time' || ex.frequency === 'custom') {
                if (!ex.date) return false;
                const exDate = new Date(ex.date);
                exDate.setHours(0,0,0,0);
                const sTime = new Date(start).setHours(0,0,0,0);
                const eTime = new Date(end).setHours(23,59,59,999);
                return exDate.getTime() >= sTime && exDate.getTime() <= eTime;
            }

            // 2. Handle Annual
            if (ex.frequency === 'annual') {
                if (!ex.date) return false;
                const exDate = new Date(ex.date);
                // Create a date for this year with the same month/day
                const currentYearDue = new Date(start.getFullYear(), exDate.getMonth(), exDate.getDate());
                const sTime = new Date(start).setHours(0,0,0,0);
                const eTime = new Date(end).setHours(23,59,59,999);
                return currentYearDue.getTime() >= sTime && currentYearDue.getTime() <= eTime;
            }

            // 3. Handle Monthly
            if(ex.frequency === 'monthly') {
                const sDay = start.getDate(); const eDay = end.getDate();
                if (start.getMonth() !== end.getMonth()) return ex.dayDue >= sDay || ex.dayDue <= eDay;
                else return ex.dayDue >= sDay && ex.dayDue <= eDay;
            }

            // 4. Handle Bi-Weekly
            if(ex.frequency === 'bi-weekly') { 
                // Simplified: Assume aligned with payday for now, or always show
                if (ex.timing === 'payday' || !ex.timing) return true; 
                return true; 
            }
            
            return ['weekly'].includes(ex.frequency); 
        }); 
        
        const actuals = data.transactions.filter(t => { 
            const d = new Date(t.date); const dTime = d.setHours(0,0,0,0);
            const sTime = new Date(start).setHours(0,0,0,0); const eTime = new Date(end).setHours(0,0,0,0);
            return dTime >= sTime && dTime <= eTime; 
        }); 
        
        const unpaidCreditWarning = data.transactions.some(t => { const tDate = new Date(t.date); return t.isCredit && !t.paidOff && tDate < start; });
        
        const spent = actuals.reduce((s, t) => s + (t.isInflow ? 0 : (t.amount || 0)), 0); 
        const periodInflows = actuals.reduce((s, t) => s + (t.isInflow ? (t.amount || 0) : 0), 0);

        const expectedSum = expected.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
        const cats = {}; actuals.forEach(t => { if(!t.isInflow) cats[t.category] = (cats[t.category]||0) + (t.amount || 0) }); 
        const pieData = Object.keys(cats).map(k => ({ name: k, value: cats[k] })); 
        const periodIncome = calculatedPaycheck.netPerPeriod; 
        
        let previousPeriodEnd = new Date(start);
        previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);
        
        const prevEndStr = toLocalDateStr(previousPeriodEnd);

        const previousHistoryItem = data.trackerHistory.find(h => {
            return toLocalDateStr(new Date(h.end)) === prevEndStr;
        });

        let chainedAdjustment = 0;
        if (previousHistoryItem && previousHistoryItem.breakdown) {
            const bd = previousHistoryItem.breakdown;
            if (bd.reduce > 0) chainedAdjustment += bd.reduce;
            if (bd.carryover > 0) chainedAdjustment -= bd.carryover; 
        }

        const currentStartStr = toLocalDateStr(start);
        const currentPeriodHistory = data.trackerHistory.find(h => {
            return toLocalDateStr(new Date(h.start)) === currentStartStr;
        });
        const isClosedPeriod = !!currentPeriodHistory;

        const baseBudget = Math.max(0, periodIncome - expectedSum);
        const discretionary = data.discretionaryBudget > 0 ? data.discretionaryBudget : baseBudget;
        
        const adjustedBudget = Math.max(0, discretionary - chainedAdjustment + periodInflows);
        const remaining = adjustedBudget - spent;

        let isDirty = false;
        if (isClosedPeriod) {
            const historySurplus = parseFloat(currentPeriodHistory.surplus) || 0;
            if (Math.abs(remaining - historySurplus) > 0.01) {
                isDirty = true;
            }
        }

        let gapDetected = false;
        if (data.trackerHistory.length > 0 && !previousHistoryItem) {
            const latestHistory = [...data.trackerHistory].sort((a,b) => new Date(b.end) - new Date(a.end))[0];
            if (new Date(latestHistory.end).getTime() < start.getTime()) {
                gapDetected = true;
            }
        }

        return { 
            start, end, expected, actuals, spent, expectedSum, remaining, pieData, 
            periodIncome, suggestedBudget: adjustedBudget, unpaidCreditWarning, 
            pendingAdjustmentApplied: chainedAdjustment,
            isDirty, 
            gapDetected,
            currentHistoryItem: currentPeriodHistory,
            targetDate: previousPeriodEnd,
            periodInflows
        }; 
    }, [data, trackerOffset, calculatedPaycheck]);

    const elementalHealth = useMemo(() => {
        const alerts = [];
        
        const isCurrentPeriodClosed = trackerData.currentHistoryItem && trackerOffset === 0;
        const hasOpenOverspend = trackerData.remaining < 0 && !isCurrentPeriodClosed;

        if (hasOpenOverspend) {
            alerts.push({ 
                element: 'fire', 
                level: 'critical', 
                message: `Over budget by $${Math.abs(trackerData.remaining).toFixed(2)}. Extinguish now.`, 
                link: 'tracker' 
            });
        } else if (trackerData.unpaidCreditWarning) { 
            alerts.push({ element: 'fire', level: 'warning', message: 'Unpaid credit transactions detected.', link: 'tracker' });
        }
        
        const sinkingFundAlert = data.sinkingFunds.some(sf => { if (!sf.date) return false; return new Date(sf.date) < new Date() && (parseFloat(sf.amount) > parseFloat(sf.current)); });
        const surplus = surplusCalculations.operational || 0;
        
        if (surplus > 0 && trackerData.remaining >= 0) alerts.push({ element: 'water', level: 'info', message: 'Surplus detected. Allocate to reservoirs or waterfall.', link: 'waterfall' });
        if (sinkingFundAlert) alerts.push({ element: 'water', level: 'warning', message: 'Reservoir target date reached.', link: 'waterfall' });
        
        const zeroAssets = data.assets.some(a => parseFloat(a.value) === 0);
        const oldestAsset = data.assets.reduce((oldest, current) => {
            if (!current.lastUpdated) return current;
            if (!oldest) return current;
            const currentTs = new Date(current.lastUpdated).getTime();
            const oldestTs = new Date(oldest.lastUpdated || 0).getTime(); 
            return currentTs < oldestTs ? current : oldest;
        }, null); 
        
        let staleAssetAlert = null;
        if (oldestAsset) {
            const lastUpdatedDate = new Date(oldestAsset.lastUpdated || 0);
            const diff = (new Date() - lastUpdatedDate) / (1000 * 60 * 60 * 24); 
            if (diff > 30 || !oldestAsset.lastUpdated) {
                const days = Math.floor(diff);
                const message = !oldestAsset.lastUpdated ? `Asset '${oldestAsset.name}' needs initial update.` : `Asset values haven't been reviewed in ${days} days.`;
                staleAssetAlert = { element: 'earth', level: 'warning', message: message, link: 'allocation' };
            }
        }
        
        if (zeroAssets) alerts.push({ element: 'earth', level: 'info', message: 'Some assets have $0 balance. Update values.', link: 'allocation' });
        else if (staleAssetAlert) alerts.push(staleAssetAlert);
        
        const visionDiff = (new Date() - new Date(data.lastVisionReview || 0)) / (1000 * 60 * 60 * 24);
        if (visionDiff > 90) alerts.push({ element: 'air', level: 'info', message: 'Quarterly Vision Check. Is your trajectory still accurate?', link: 'dashboard' });
        
        return { alerts, hasCritical: alerts.some(a => a.level === 'critical') };
    }, [trackerData, surplusCalculations, data.sinkingFunds, data.assets, data.lastVisionReview, trackerOffset]);

    const projections = useMemo(() => {
        let age = Number(data.age) || 0; 
        let year = new Date().getFullYear(); 
        let salary = Number(data.salary) || 0; 
        
        const annualFixedBurn = (Number(expenseAnalysis.burn) || 0) * 12;
        
        const monthlyVariable = data.discretionaryBudget > 0 
            ? data.discretionaryBudget 
            : Math.max(0, (calculatedPaycheck.monthlyNet - (Number(expenseAnalysis.burn) || 0)));

        const annualVariableBurn = monthlyVariable * 12;

        let annualTotalBurn = annualFixedBurn + annualVariableBurn;

        const inflationRate = Number(data.inflation) || 0;
        const salaryGrowth = Number(data.salaryGrowth) || 0;
        const safeRate = Number(data.withdrawalRate) || 4.0;
        
        let baseAnnualPassiveIncome = 0;
        (data.additionalIncome || []).forEach(stream => { if (stream.isPassive) { const net = calculateAdditionalIncomeNet(stream); baseAnnualPassiveIncome += (net * 12); } });

        let assets = data.assets.map(a => ({ ...a, id: a.id, accumulated: 0, value: Number(a.value) || 0 })); 
        let assetsNoBonus = data.assets.map(a => ({ ...a, id: a.id, accumulated: 0, value: Number(a.value) || 0 })); 
        let debts = data.debts.map(d => ({...d, balance: Number(d.balance) || 0})); 

        const points = [];
        const lifeExpectancy = Number(data.lifeExpectancy) || 90;
        let currentPassive = baseAnnualPassiveIncome;

        const initialAssetKeys = {};
        let initialNetWorth = 0;
        let initialNetWorthNoBonus = 0;
        
        assets.forEach(a => {
            initialAssetKeys[`asset_${a.id}`] = Math.round(a.value);
            initialNetWorth += a.value;
            initialNetWorthNoBonus += a.value;
        });
        
        const initialTotalDebt = debts.filter(d => d.includeInNetWorth !== false).reduce((s,d) => s + d.balance, 0);
        
        const initialNetAnnualBurn = Math.max(0, annualTotalBurn - currentPassive);
        
        points.push({
            age,
            year,
            netWorth: Math.round(initialNetWorth - initialTotalDebt),
            netWorthNoBonus: Math.round(initialNetWorthNoBonus - initialTotalDebt),
            fiNumber: Math.round(initialNetAnnualBurn / (safeRate/100)),
            totalDebt: Math.round(initialTotalDebt),
            ...initialAssetKeys
        });

        age++;
        year++;

        while (age <= lifeExpectancy) {
            const retired = age >= data.retireAge;
            if (!retired) {
                data.deductions.forEach(d => { if (d.linkedAssetId) { let contrib = d.type === 'fixed' ? (d.category==='Tax'?0:(Number(d.amount)||0)*(data.payFrequency||26)) : salary*((Number(d.amount)||0)/100); if (d.category === 'Retirement' && d.employerMatchLimit) contrib += salary * (Math.min(Number(d.amount)||0, d.employerMatchLimit)/100); const a = assets.find(x => x.id === d.linkedAssetId); if(a) a.accumulated += contrib; const b = assetsNoBonus.find(x => x.id === d.linkedAssetId); if(b) b.accumulated += contrib; } });
                data.bonuses.forEach(b => { if(b.active && b.linkedAssetId) { const raw = b.type === 'fixed' ? (Number(b.amount)||0) : salary * ((Number(b.amount)||0)/100); const net = raw * (1 - ((Number(b.taxRate)||0)/100)); const a = assets.find(x => x.id === b.linkedAssetId); if(a) a.accumulated += net; } });
                (data.additionalIncome || []).forEach(stream => {
                    const gross = normalizeToMonthly(parseFloat(stream.amount)||0, stream.frequency);
                    if (stream.inputType === 'gross') { (stream.deductions || []).forEach(d => { if (d.linkedAssetId) { let amt = parseFloat(d.amount)||0; let contrib = d.type === 'percent' ? gross * (amt / 100) : normalizeToMonthly(amt, d.frequency || stream.frequency); contrib *= 12; const a = assets.find(x => x.id === d.linkedAssetId); if(a) a.accumulated += contrib; const b = assetsNoBonus.find(x => x.id === d.linkedAssetId); if(b) b.accumulated += contrib; } }); }
                    if (stream.flowTo && stream.flowTo !== 'budget') { const netMonthly = calculateAdditionalIncomeNet(stream); const annualContrib = netMonthly * 12; const a = assets.find(x => x.id === Number(stream.flowTo)); if(a) a.accumulated += annualContrib; const b = assetsNoBonus.find(x => x.id === Number(stream.flowTo)); if(b) b.accumulated += annualContrib; }
                });
                const gross = calculatedPaycheck.grossPerPeriod || 1; const taxRate = 1 - ((calculatedPaycheck.netPerPeriod || 0) / gross); 
                let annualBaseNet = (salary * (1-taxRate)); let annualAdditionalBudgetIncome = 0;
                (data.additionalIncome || []).forEach(stream => { if (!stream.flowTo || stream.flowTo === 'budget') { annualAdditionalBudgetIncome += (calculateAdditionalIncomeNet(stream) * 12); } });
                const totalNet = annualBaseNet + annualAdditionalBudgetIncome - (annualTotalBurn/12);
                
                if (totalNet > 0) { let s = totalNet; let sNoBonus = totalNet; data.waterfall.sort((a,b)=>a.priority-b.priority).forEach(w => { if(s > 0) { const a = assets.find(x=>x.id === w.linkedAssetId); if(a) { const fill = w.targetType === 'annual_limit' ? Math.min(s, Number(w.targetAmount)||0) : s; a.accumulated += fill; s -= fill; } } if(sNoBonus > 0) { const b = assetsNoBonus.find(x=>x.id === w.linkedAssetId); if(b) { const fill = w.targetType === 'annual_limit' ? Math.min(sNoBonus, Number(w.targetAmount)||0) : sNoBonus; b.accumulated += fill; sNoBonus -= fill; } } }); }
            }
            
            debts.forEach(d => { if (d.balance > 0) { const pay = ((d.minPayment||0) + (Number(d.extraPrincipal)||0)) * 12; const int = d.balance * ((d.rate||0) / 100); d.balance = Math.max(0, d.balance + int - pay); } });
            
            let netWorth = 0; let netWorthNoBonus = 0; 
            const netAnnualBurn = Math.max(0, annualTotalBurn - currentPassive);
            const currentTotalAssets = assets.reduce((sum, x) => sum + x.value, 0) || 1;
            const requiredDrawdownPct = retired ? (netAnnualBurn / currentTotalAssets) * 100 : 0;
            
            assets.forEach((a, idx) => { 
                let rate = Number(a.returnRate) || 0;
                if (simulateVolatility) { const u = 1 - Math.random(); const v = Math.random(); const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v); rate = rate + (z * ((Number(a.volatility) || 10))); }
                let growthFactor = 1 + rate/100;
                if (retired) growthFactor = 1 + (rate - requiredDrawdownPct)/100;
                a.value = (a.value * growthFactor) + a.accumulated; if(isNaN(a.value)) a.value = 0; a.accumulated = 0; netWorth += a.value;
                let b = assetsNoBonus[idx]; b.value = (b.value * growthFactor) + b.accumulated; if(isNaN(b.value)) b.value = 0; b.accumulated = 0; netWorthNoBonus += b.value;
            });
            
            const totalDebtForNW = debts.filter(d => d.includeInNetWorth !== false).reduce((s,d)=>s+d.balance, 0);
            const assetKeys = {}; data.assets.forEach(a => { assetKeys[`asset_${a.id}`] = Math.round(assets.find(live => live.id === a.id)?.value || 0); });
            
            points.push({ 
                age, 
                year, 
                netWorth: Math.round(netWorth - totalDebtForNW), 
                netWorthNoBonus: Math.round(netWorthNoBonus - totalDebtForNW), 
                fiNumber: Math.round(netAnnualBurn / (safeRate/100)), 
                totalDebt: Math.round(debts.reduce((s,d)=>s+d.balance, 0)), 
                ...assetKeys 
            });
            
            age++; year++; 
            salary *= (1 + (salaryGrowth||0)/100); 
            annualTotalBurn *= (1 + (inflationRate||0)/100); 
            currentPassive *= (1 + (inflationRate||0)/100);
        }
        return points;
    }, [data, expenseAnalysis, calculatedPaycheck, simulateVolatility]);

    const retirementData = projections.find(d => d.age === data.retireAge);
    const successRatio = (retirementData && retirementData.fiNumber > 0) ? (retirementData.netWorth / retirementData.fiNumber) * 100 : (retirementData?.netWorth > 0 ? 100 : 0);
    const currentNetWorth = data.assets.reduce((s,a)=>s+parseFloat(a.value || 0),0) - data.debts.filter(d => d.includeInNetWorth !== false).reduce((s,d)=>s+parseFloat(d.balance || 0),0);

    return { 
        calculatedPaycheck, 
        expenseAnalysis, 
        surplusCalculations, 
        waterfallVisuals, 
        trackerData, 
        projections, 
        successRatio, 
        retirementData, 
        currentNetWorth, 
        globalAllocation, 
        elementalHealth 
    };
};